import "../../components/EventUserDashboard.css";

import React, { useEffect, useMemo, useState } from "react";
import { Select } from "@mantine/core";
import QRCode from "react-qr-code";

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface DrinkTicketCollectionDto {
    drinkTicketCollectionId: number;
    collectionName: string;
    bar: number;
    maxDrinksPerTicket: number;
    ticketCount: number;
    createdAt?: string;
    validDate?: string | null;
}

interface DrinkTicketDto {
    drinkTicketId: number;
    ticketCode: string;
    collectionName: string;
    ticketNumber: number;
    bar: number;
    maxDrinks: number;
    isActive: boolean;
}

enum Bar {
    BAR1 = 1,
    BAR2 = 2,
    BAR3 = 3
}

const getBarName = (bar: number) => {
    switch (bar) {
        case Bar.BAR1:
            return "VIP Lounge";
        case Bar.BAR2:
            return "Hygge Lounge";
        case Bar.BAR3:
            return "Jail House Bar";
        default:
            return `Bar ${bar}`;
    }
};

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.results)) return result.results;
    return [];
};

const formatDate = (value?: string | null) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

const AdminDrinkTicketPrint: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL;

    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);

    const [collections, setCollections] = useState<DrinkTicketCollectionDto[]>([]);
    const [selectedCollectionId, setSelectedCollectionId] = useState<number>(0);
    const [tickets, setTickets] = useState<DrinkTicketDto[]>([]);

    const [error, setError] = useState<string | null>(null);
    const [loadingCollections, setLoadingCollections] = useState(false);
    const [loadingTickets, setLoadingTickets] = useState(false);

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetch(`${apiUrl}/api/events`);
                if (!res.ok) return;

                const result = await res.json();
                setEvents(toArray<EventDto>(result));
            } catch (err) {
                console.error("Fejl ved hentning af events:", err);
            }
        };

        loadEvents();
    }, [apiUrl]);

    const loadCollections = async (currentEventId: number) => {
        if (!currentEventId) {
            setCollections([]);
            return;
        }

        try {
            setLoadingCollections(true);
            setError(null);

            const res = await fetch(`${apiUrl}/api/admin/drink-ticket-collections?eventId=${currentEventId}`);
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Kunne ikke hente collections");
            }

            const result = await res.json();
            setCollections(toArray<DrinkTicketCollectionDto>(result));
        } catch (err: any) {
            console.error("Fejl ved hentning af collections:", err);
            setCollections([]);
            setError(err?.message || "Kunde ikke hente collections");
        } finally {
            setLoadingCollections(false);
        }
    };

    const loadTickets = async (collectionId: number) => {
        if (!collectionId) {
            setTickets([]);
            return;
        }

        try {
            setLoadingTickets(true);
            setError(null);

            const res = await fetch(`${apiUrl}/api/admin/drink-ticket-collections/${collectionId}/tickets`);
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Kunne ikke hente billetter");
            }

            const result = await res.json();
            setTickets(toArray<DrinkTicketDto>(result));
        } catch (err: any) {
            console.error("Fejl ved hentning af billetter:", err);
            setTickets([]);
            setError(err?.message || "Kunde ikke hente billetter");
        } finally {
            setLoadingTickets(false);
        }
    };

    useEffect(() => {
        setSelectedCollectionId(0);
        setTickets([]);
        setError(null);

        if (eventId > 0) {
            loadCollections(eventId);
        } else {
            setCollections([]);
        }
    }, [eventId]);

    useEffect(() => {
        if (selectedCollectionId > 0) {
            loadTickets(selectedCollectionId);
        } else {
            setTickets([]);
        }
    }, [selectedCollectionId]);

    const selectedCollection = useMemo(() => {
        return collections.find((x) => x.drinkTicketCollectionId === selectedCollectionId) ?? null;
    }, [collections, selectedCollectionId]);

    const printPage = () => {
        window.print();
    };

    const getQrValue = (ticket: DrinkTicketDto) => {
        return `${window.location.origin}/form-drink-order/${eventId}/${encodeURIComponent(ticket.ticketCode)}?bar=${ticket.bar}`;
    };

    return (
        <div className="dashContainer">
            <div className="wrapper">
                <div className="top no-print">
                    <div className="name">Print billetkoder</div>
                    <div className="meta">Vælg event og collection for at printe QR-koder</div>
                </div>

                <div className="content">
                    {error && (
                        <div className="badgeRed no-print" style={{ padding: 8, marginBottom: 16 }}>
                            {error}
                        </div>
                    )}

                    <div className="no-print">
                        <div className="sectionTitle">Valg</div>

                        <div className="statsGrid3" style={{ marginTop: 12 }}>
                            <div className="stat" style={{ padding: 16 }}>
                                <Select
                                    placeholder="Vælg event"
                                    value={eventId ? String(eventId) : null}
                                    onChange={(val) => setEventId(val ? Number(val) : 0)}
                                    data={events.map((e) => ({
                                        value: String(e.eventID),
                                        label: e.eventName || `Event ${e.eventID}`
                                    }))}
                                />
                            </div>

                            <div className="stat" style={{ padding: 16 }}>
                                <Select
                                    searchable
                                    clearable
                                    placeholder={loadingCollections ? "Henter collections..." : "Vælg collection"}
                                    value={selectedCollectionId ? String(selectedCollectionId) : null}
                                    onChange={(val) => setSelectedCollectionId(val ? Number(val) : 0)}
                                    data={collections.map((c) => ({
                                        value: String(c.drinkTicketCollectionId),
                                        label: `${c.collectionName} · ${getBarName(c.bar)} · ${c.ticketCount} stk${c.validDate ? ` · ${formatDate(c.validDate)}` : ""}`
                                    }))}
                                />
                            </div>

                            <div className="stat" style={{ padding: 16, display: "flex", alignItems: "end" }}>
                                <button
                                    type="button"
                                    style={{ width: "100%" }}
                                    onClick={printPage}
                                    disabled={!selectedCollection || tickets.length === 0}
                                >
                                    Print
                                </button>
                            </div>
                        </div>
                    </div>

                    {selectedCollection && (
                        <div className="print-only-area" style={{ marginTop: 12 }}>
                            {loadingTickets ? (
                                <div className="no-print" style={{ padding: 16 }}>Henter billetter...</div>
                            ) : (
                                <div className="tickets-print-grid">
                                    {tickets.map((ticket) => (
                                        <div
                                            key={ticket.drinkTicketId}
                                            className="ticket-print-card"
                                        >
                                            <div className="ticket-qr-wrap">
                                                <QRCode
                                                    value={getQrValue(ticket)}
                                                    size={190}
                                                    bgColor="#FFFFFF"
                                                    fgColor="#000000"
                                                    level="M"
                                                />
                                            </div>

                                            <div className="ticket-code-text">
                                                {ticket.ticketCode?.replace(/-\d+-/, "-")}
                                            </div>

                                            {selectedCollection.validDate && (
                                                <div className="ticket-date-text">
                                                    Only valid {formatDate(selectedCollection.validDate)}
                                                </div>
                                            )}

                                            <div className="ticket-bar-text">
                                                {getBarName(ticket.bar)}
                                            </div>

                                            <div className="ticket-drinks-text">
                                                Drinks: {ticket.maxDrinks}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .tickets-print-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                    gap: 12px;
                    padding: 8px;
                }

                .ticket-print-card {
                    border: 1px solid rgba(0,0,0,0.12);
                    border-radius: 8px;
                    background: #fff;
                    padding: 12px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                    min-height: 260px;
                    box-sizing: border-box;
                }

                .ticket-qr-wrap {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #fff;
                    padding: 4px;
                }

                .ticket-code-text {
                    margin-top: 8px;
                    font-size: 16px;
                    font-weight: 800;
                    word-break: break-word;
                    line-height: 1.1;
                }

                .ticket-collection-text {
                    margin-top: 4px;
                    font-size: 12px;
                    font-weight: 700;
                    line-height: 1.1;
                }

                .ticket-date-text {
                    margin-top: 2px;
                    font-size: 11px;
                    line-height: 1.1;
                    font-weight: 700;
                }

                .ticket-bar-text {
                    margin-top: 4px;
                    font-size: 12px;
                }

                .ticket-drinks-text {
                    margin-top: 2px;
                    font-size: 12px;
                }

                @media print {
                    @page {
                        size: A4;
                        margin: 4mm;
                    }

                    body * {
                        visibility: hidden;
                    }

                    .print-only-area,
                    .print-only-area * {
                        visibility: visible;
                    }

                    .print-only-area {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        margin: 0 !important;
                        padding: 0 !important;
                    }

                    .no-print {
                        display: none !important;
                    }

                    .dashContainer,
                    .wrapper,
                    .content {
                        padding: 0 !important;
                        margin: 0 !important;
                        background: white !important;
                    }

                    .tickets-print-grid {
                        display: grid !important;
                        grid-template-columns: repeat(4, 1fr) !important;
                        gap: 0 !important;
                        padding: 0 !important;
                        width: 100% !important;
                    }

                    .ticket-print-card {
                        height: 71mm !important;
                        min-height: 71mm !important;
                        border: 1px solid #000 !important;
                        border-radius: 0 !important;
                        box-shadow: none !important;
                        padding: 2mm !important;
                        margin: 0 !important;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }

                    .ticket-qr-wrap {
                        padding: 0 !important;
                        margin: 0 !important;
                    }

                    .ticket-qr-wrap svg {
                        width: 120px !important;
                        height: 120px !important;
                    }

                    .ticket-code-text {
                        margin-top: 2mm !important;
                        font-size: 10px !important;
                        font-weight: 800 !important;
                        line-height: 1.05 !important;
                    }

                    .ticket-collection-text {
                        margin-top: 1mm !important;
                        font-size: 8.5px !important;
                        font-weight: 700 !important;
                        line-height: 1 !important;
                    }

                    .ticket-date-text {
                        margin-top: 1mm !important;
                        font-size: 8px !important;
                        line-height: 1 !important;
                        font-weight: 800 !important;
                    }

                    .ticket-bar-text {
                        margin-top: 1mm !important;
                        font-size: 8px !important;
                        line-height: 1 !important;
                    }

                    .ticket-drinks-text {
                        margin-top: 1mm !important;
                        font-size: 8px !important;
                        line-height: 1 !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default AdminDrinkTicketPrint;